import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clientCancellationLineService from '../../services/clientCancellationLines/ClientCancellationLineService';
import type {
  ClientCancellationLineQueueQuery,
  CreateClientCancellationLineRequest,
  PatchClientCancellationLineStatusRequest,
} from '../../types/clientCancellationLines';
import {
  CLIENT_CANCELLATION_STALE_MS,
  clientCancellationQueryKeys,
} from './clientCancellationLinesQueryKeys';

export function useSuspensionBlockStatusQuery(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: clientCancellationQueryKeys.suspensionBlock(),
    queryFn: () => clientCancellationLineService.getSuspensionBlockStatus(),
    staleTime: CLIENT_CANCELLATION_STALE_MS,
    enabled: options?.enabled ?? true,
    retry: 1,
  });
}

export function useClientCancellationLinesByContractQuery(contractId?: string | null) {
  return useQuery({
    queryKey: clientCancellationQueryKeys.byContract(contractId ?? ''),
    queryFn: () => clientCancellationLineService.listByContract(contractId!),
    enabled: !!contractId,
    staleTime: CLIENT_CANCELLATION_STALE_MS,
    retry: 1,
  });
}

export function useClientCancellationQueueQuery(query: ClientCancellationLineQueueQuery, enabled = true) {
  return useQuery({
    queryKey: clientCancellationQueryKeys.queue(query),
    queryFn: () => clientCancellationLineService.getQueue(query),
    enabled,
    staleTime: 30_000,
    retry: 1,
  });
}

export function useClientCancellationMutations(contractId?: string | null) {
  const qc = useQueryClient();
  const invalidateContract = () => {
    if (contractId) {
      void qc.invalidateQueries({ queryKey: clientCancellationQueryKeys.byContract(contractId) });
    }
    void qc.invalidateQueries({ queryKey: ['client-cancellation-lines', 'queue'] });
  };

  const createMutation = useMutation({
    mutationFn: (payload: CreateClientCancellationLineRequest) =>
      clientCancellationLineService.create(contractId!, payload),
    onSuccess: invalidateContract,
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => clientCancellationLineService.cancelEntry(id),
    onSuccess: invalidateContract,
  });

  const patchStatusMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: PatchClientCancellationLineStatusRequest }) =>
      clientCancellationLineService.patchStatus(id, payload),
    onSuccess: invalidateContract,
  });

  return { createMutation, cancelMutation, patchStatusMutation };
}
